import { computed, Signal } from '@angular/core';

/**
 * Executes a callback function only if the signal value is defined (not null or undefined)
 * @param signal A function that returns a value (signal)
 * @param callback Function to execute with the defined value
 */
export function withDefinedSignal<T>(signal: () => T | undefined | null, callback: (value: T) => void): void {
  const value = signal();
  if (value !== undefined && value !== null) {
    callback(value);
  }
}

/**
 * Strongly-typed version: Combines multiple signals (provided as an object) and returns a computed signal that emits an object
 * with the same keys and their values only when all are defined (not null or undefined), otherwise returns null.
 */
export function combineDefinedSignalsObj<T extends Record<string, Signal<any>>>(
  signalsObj: T,
): Signal<{ [K in keyof T]: T[K] extends Signal<infer V> ? NonNullable<V> : never } | null> {
  return computed(() => {
    const result = {} as { [K in keyof T]: T[K] extends Signal<infer V> ? NonNullable<V> : never };
    for (const key in signalsObj) {
      const value = signalsObj[key]();
      if (value === undefined || value === null) return null;
      result[key] = value;
    }
    return result;
  });
}
